import {
  Icon,
  MenuGroup,
  MenuList,
  MenuToggle,
  Menu,
  MenuItem,
  Flex,
} from '@tonic-one/react';
import { MoreIcon } from '@tonic-one/react-icons';
import {
  DataGrid,
  DATA_GRID_CHECKBOX_SELECTION_COLUMN_DEF,
  DataGridProps,
} from '@tonic-one/react-data-grid';
import { memo, useMemo, useCallback } from 'react';
import {
  ActionModalState,
  EuqFeedbackRecord,
  FeedbackFilterStatus,
} from '../../type';
import { useOrgStore } from '@Models/useOrgModel';
import { useTranslation } from 'react-i18next';
import { useUtcOffsetStore } from '@Models/useUtcOffsetModel';
import { getUtcTime } from '@Utils/time';
import { usePermissionStore } from '@Models/usePermissionModel';
import { PermissionNameEnum } from '@Types/permissions';
import { isInUIC } from '@Utils/common';
import { handleBlockSenderCtxMenu } from '@Utils/ctxMenu';
import TextEllipsis, {
  TextEllipsisProps,
} from '@Components/Common/Tooltip/TextEllipsis';
import { FeedbackCategoryStrMap } from '@Quarantine/UserReportedEmails/constants';

interface DetailsTableCustomProps {
  sortKey: string;
  setSortKey: (key: string) => void;
  actionType?: FeedbackFilterStatus;
  setActionModalState?: (v: ActionModalState) => void;
  enableSelection?: boolean;
}

interface DetailsTableProps
  extends Omit<DataGridProps, 'columns'>,
    DetailsTableCustomProps {}

export const DetailsTable = memo(
  ({
    data,
    sortKey,
    setSortKey,
    actionType,
    setActionModalState,
    enableSelection = false,
    rowSelection,
    onRowSelectionChange,
    isLoading = false,
    pagination,
    onPaginationChange,
    rowCount,
    pageSizeOptions = [10, 20, 50, 100],
  }: DetailsTableProps) => {
    const { t } = useTranslation();
    const { offset } = useUtcOffsetStore();
    const timeFormat = t('common.momentDateFormat');
    const { orgsMap } = useOrgStore();
    const { widthEditablePermission } = usePermissionStore();
    const qtEditPermission = widthEditablePermission(
      PermissionNameEnum.quarantine
    );

    const columns = useMemo(() => {
      const baseColumns = [];

      const Ellipsis = ({ children, ...rest }: TextEllipsisProps) => (
        <TextEllipsis popperProps={{ usePortal: true }} {...rest}>
          {children}
        </TextEllipsis>
      );

      // Add selection column if enabled
      if (enableSelection) {
        baseColumns.push({
          ...DATA_GRID_CHECKBOX_SELECTION_COLUMN_DEF,
          width: 40,
          minWidth: 40,
        });
      }

      baseColumns.push(
        {
          id: 'created_at',
          header: t('operations.euq.detail.table.header.reportedAt'),
          accessorFn: (row: EuqFeedbackRecord) =>
            getUtcTime(row.created_at, offset, timeFormat),
          width: '13%',
          isSortable: true,
          cell: ({ row }: { row: { value: EuqFeedbackRecord } }) => (
            <Ellipsis>
              {getUtcTime(row.value.created_at, offset, timeFormat)}
            </Ellipsis>
          ),
        },
        {
          id: 'message_subject',
          header: t('operations.euq.detail.table.header.subject'),
          accessorKey: 'message_subject',
          width: '10%',
          isSortable: true,
          cell: ({ row }: { row: { value: EuqFeedbackRecord } }) => (
            <Ellipsis>{row.value.message_subject}</Ellipsis>
          ),
        },
        {
          id: 'company_guid',
          header: t('operations.euq.detail.table.header.org'),
          accessorFn: (row: EuqFeedbackRecord) =>
            orgsMap.get(row.company_guid) || '',
          width: '10%',
          isSortable: true,
          cell: ({ row }: { row: { value: EuqFeedbackRecord } }) => (
            <Ellipsis>{orgsMap.get(row.value.company_guid) || ''}</Ellipsis>
          ),
        },
        {
          id: 'requested_by',
          header: t('operations.euq.detail.table.header.reporter'),
          accessorKey: 'requested_by',
          width: '15%',
          isSortable: true,
          cell: ({ row }: { row: { value: EuqFeedbackRecord } }) => (
            <Ellipsis>{row.value.requested_by}</Ellipsis>
          ),
        },
        {
          id: 'category',
          header: t('operations.euq.detail.table.header.reportedAs'),
          accessorFn: (row: EuqFeedbackRecord) =>
            t(
              FeedbackCategoryStrMap[
                row.category as keyof typeof FeedbackCategoryStrMap
              ],
              row.category
            ),
          width: '10%',
          isSortable: true,
          cell: ({ row }: { row: { value: EuqFeedbackRecord } }) => (
            <Ellipsis>
              {t(
                FeedbackCategoryStrMap[
                  row.value.category as keyof typeof FeedbackCategoryStrMap
                ],
                row.value.category
              )}
            </Ellipsis>
          ),
        },
        {
          id: 'message_arrive_time',
          header: t('operations.euq.detail.table.header.arrivingTime'),
          accessorFn: (row: EuqFeedbackRecord) =>
            getUtcTime(row.message_arrive_time, offset, timeFormat),
          width: '13%',
          isSortable: true,
          cell: ({ row }: { row: { value: EuqFeedbackRecord } }) => (
            <Ellipsis>
              {getUtcTime(row.value.message_arrive_time, offset, timeFormat)}
            </Ellipsis>
          ),
        },
        {
          id: 'message_sender',
          header: t('operations.euq.detail.table.header.sender'),
          accessorKey: 'message_sender',
          width: '15%',
          isSortable: true,
          cell: ({ row }: { row: { value: EuqFeedbackRecord } }) => (
            <Ellipsis>{row.value.message_sender}</Ellipsis>
          ),
        },
        {
          id: 'message_recipients',
          header: t('operations.euq.detail.table.header.recipient'),
          accessorKey: 'message_recipients',
          width: '15%',
          isSortable: true,
          cell: ({ row }: { row: { value: EuqFeedbackRecord } }) => (
            <Ellipsis>{row.value.message_recipients}</Ellipsis>
          ),
        },
        {
          id: 'internet_message_id',
          header: t('operations.euq.detail.table.header.messageID'),
          accessorKey: 'internet_message_id',
          width: '10%',
          isSortable: true,
          cell: ({ row }: { row: { value: EuqFeedbackRecord } }) => (
            <Ellipsis>{row.value.internet_message_id}</Ellipsis>
          ),
        },
        {
          id: 'request_source',
          header: t('administration.addIn.autoCollect.collectSource.column'),
          accessorFn: (row: EuqFeedbackRecord) =>
            row.request_source ?? 'Plugin',
          width: '8%',
          isSortable: true,
          cell: ({ row }: { row: { value: EuqFeedbackRecord } }) => {
            const src = row.value.request_source ?? 'Plugin';
            const labelKey =
              src === 'AutoCollect'
                ? 'administration.addIn.autoCollect.collectSource.autoCollect'
                : src === 'Banner'
                  ? 'administration.addIn.autoCollect.collectSource.banner'
                  : src === 'CentralOperation'
                    ? 'administration.addIn.autoCollect.collectSource.adminReport'
                    : 'administration.addIn.autoCollect.collectSource.plugin';
            return <Ellipsis>{t(labelKey)}</Ellipsis>;
          },
        }
      );

      // Add action column if actions are enabled
      if (actionType && setActionModalState) {
        const actionEnabled = actionType === FeedbackFilterStatus.Created;
        const ctxMenuEnabled = isInUIC();

        // Only show actions if conditions are met
        if (isInUIC() || actionType === FeedbackFilterStatus.Created) {
          baseColumns.push({
            id: 'actions',
            width: '65px',
            isSortable: false,
            cell: ({ row }: { row: { value: EuqFeedbackRecord } }) => {
              const { id, message_sender: sender } = row.value;
              const menuItemStyle = {
                pl: '24px',
                disabled: !qtEditPermission,
              };
              return (
                <Flex justifyContent="center" alignItems="center">
                  <Menu placement="bottom-end">
                    <MenuToggle _hover={{ color: '#578aef' }}>
                      <Icon as={MoreIcon} />
                    </MenuToggle>
                    <MenuList
                      PopperProps={{
                        modifiers: [
                          {
                            name: 'flip',
                          },
                        ],
                        usePortal: true,
                      }}
                    >
                      {actionEnabled && (
                        <MenuGroup
                          title={t('common.ctxMenu.ReportToTM')}
                          textTransform="uppercase"
                        >
                          <MenuItem
                            onClick={() => {
                              setActionModalState({
                                id: `${id}`,
                                name: 'approve',
                              });
                            }}
                            {...menuItemStyle}
                          >
                            {t('operations:operations.euq.action.approve.btn')}
                          </MenuItem>
                          <MenuItem
                            onClick={() => {
                              setActionModalState({
                                id: `${id}`,
                                name: 'reject',
                              });
                            }}
                            {...menuItemStyle}
                          >
                            {t('operations:operations.euq.action.reject.btn')}
                          </MenuItem>
                        </MenuGroup>
                      )}
                      {ctxMenuEnabled && (
                        <MenuGroup
                          title={t('common.ctxMenu.senderAction')}
                          textTransform="uppercase"
                        >
                          <MenuItem
                            {...menuItemStyle}
                            onClick={() => {
                              handleBlockSenderCtxMenu(sender);
                            }}
                          >
                            {t('common.ctxMenu.blockSender')}
                          </MenuItem>
                        </MenuGroup>
                      )}
                    </MenuList>
                  </Menu>
                </Flex>
              );
            },
          });
        }
      }

      return baseColumns;
    }, [
      enableSelection,
      actionType,
      setActionModalState,
      t,
      offset,
      timeFormat,
      orgsMap,
      qtEditPermission,
    ]);

    const sorting = useMemo(() => {
      const isDesc = sortKey.startsWith('-');
      const columnId = isDesc ? sortKey.slice(1) : sortKey;
      return {
        columnId,
        direction: isDesc ? 'desc' : 'asc',
      } as const;
    }, [sortKey]);

    const handleSortingChange = useCallback(
      (newSorting: { columnId: string; direction: 'asc' | 'desc' }) => {
        if (
          sorting.columnId === 'created_at' &&
          newSorting.columnId === undefined &&
          newSorting.direction === undefined
        ) {
          setSortKey('created_at');
        } else {
          const columnId = newSorting.columnId ?? 'created_at';
          const direction = newSorting.direction ?? 'desc';
          const prefix = direction === 'desc' ? '-' : '';
          setSortKey(`${prefix}${columnId}`);
        }
      },
      [setSortKey, sorting.columnId]
    );

    return (
      <DataGrid
        data={data}
        columns={columns}
        isColumnSortable={true}
        autoSorting={false}
        sorting={sorting}
        onSortingChange={handleSortingChange}
        showPagination
        pagination={pagination}
        onPaginationChange={onPaginationChange}
        rowCount={rowCount}
        pageSizeOptions={pageSizeOptions}
        isColumnResizable={true}
        isLoading={isLoading}
        emptyContent={t('common.noData')}
        getRowId={(row) => `${row.id}`}
        rowSelection={enableSelection ? rowSelection : undefined}
        onRowSelectionChange={
          enableSelection ? onRowSelectionChange : undefined
        }
        disableAutoResetRowSelection
        disableAutoResetPageIndex
      />
    );
  }
);
